/**
 * Реферальный код перехода и идентификатор устройства.
 *
 * Оба значения — сигналы для сервера, а не решения клиента: код только
 * сообщает, по чьей ссылке пришёл человек, а реферал заводит и награждает
 * сервер (onboard_user + qualify_referral). Подделать здесь можно ровно
 * то, что и так проверяется на сервере: самореферал запрещён constraint'ом,
 * повторная награда — уникальным ключом журнала кредитов.
 */

const REFERRAL_KEY = 'referral-code';
/*
 * Отметка «пришёл по ссылке» ждёт своего часа отдельно от самого кода:
 * в момент перехода сессии ещё нет, а событие без владельца записать
 * нельзя. Код при этом живёт до первой генерации, а отметка — до первой
 * записи события.
 */
const ARRIVAL_KEY = 'referral-arrival';
const DEVICE_KEY = 'device-id';

/** Тот же алфавит, что в generate_referral_code: без похожих 0/O и 1/l. */
const CODE_PATTERN = /^[ABCDEFGHJKMNPQRSTUVWXYZ23456789]{8}$/;

/**
 * Запомнить код из ссылки приглашения.
 *
 * Первый переход решает: иначе приглашённому можно прислать вторую ссылку
 * и переписать, кому достанется награда. Хранится до первой генерации —
 * между переходом и ей проходит произвольное время и несколько загрузок
 * страницы, поэтому память переживает перезагрузку.
 */
export function captureReferralCode(href: string): void {
  const raw = new URL(href).searchParams.get('ref');

  if (!raw || storedReferralCode() !== null) {
    return;
  }

  const code = raw.trim().toUpperCase();

  if (CODE_PATTERN.test(code)) {
    safeWrite(REFERRAL_KEY, code);
    safeWrite(ARRIVAL_KEY, code);
  }
}

export function storedReferralCode(): string | null {
  const code = safeRead(REFERRAL_KEY);

  return code !== null && CODE_PATTERN.test(code) ? code : null;
}

export function clearReferralCode(): void {
  try {
    localStorage.removeItem(REFERRAL_KEY);
  } catch {
    // Приватный режим может запрещать запись — терять тут нечего.
  }
}

/**
 * Стабильный идентификатор этого браузера.
 *
 * Нужен серверу как один из сигналов ограничения стартовых начислений
 * (см. signup_grants). Это не отпечаток устройства: случайное значение,
 * которое пользователь может стереть вместе с данными сайта — тогда
 * останется ограничение по IP.
 */
export function deviceId(): string {
  const existing = safeRead(DEVICE_KEY);

  if (existing) {
    return existing;
  }

  const created = crypto.randomUUID();
  safeWrite(DEVICE_KEY, created);

  return created;
}

function safeRead(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeWrite(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Без хранилища механика деградирует, но ничего не ломает.
  }
}

/**
 * Забрать неотмеченный переход по ссылке.
 *
 * Читает и сразу забывает: событие о переходе записывается один раз, а не
 * при каждой загрузке страницы, пока код лежит в хранилище.
 */
export function takeReferralArrival(): string | null {
  const code = safeRead(ARRIVAL_KEY);

  try {
    localStorage.removeItem(ARRIVAL_KEY);
  } catch {
    // Приватный режим может запрещать запись — событие тогда повторится.
  }

  return code;
}
